import { Injectable, signal, WritableSignal } from '@angular/core';
import { environment } from '../../../environments/environment';
import { Organization } from '../../state/models/organization';
import { UrlService } from './url.service';
import { AmplitudeService } from './amplitude.service';
import { Cycles } from '../../state/models/cycles';
import { AuthStatements } from '../../state/models/auth-statements';
import { FeatureFlagService } from './feature-flag.service';

@Injectable({
  providedIn: 'root'
})
export class OrganizationService {
  private cycles: WritableSignal<Cycles> = signal(null);

  private hasIncentiveGroup: boolean | null = false;
  organization: Organization | undefined;
  organizationLast: Organization | undefined;
  isAdmin: boolean;
  isManyOrgs: boolean;
  authStatements: AuthStatements[];

  constructor(
    private featureFlagService: FeatureFlagService,
    private amplitudeService: AmplitudeService,
    private urlService: UrlService) {
  }

  get hasAdmin(): boolean {
    return this.isAdmin || false;
  }

  get hasManyOrgs(): boolean {
    return this.isManyOrgs || false;
  }

  get authStatementsList(): AuthStatements[] {
    return this.authStatements ?? [];
  }

  setCycles(cycles: Cycles) {
    this.cycles.set(cycles);
  }

  get cycle(): Cycles {
    return this.cycles();
  }

  setInfoPermissions(extras?: { hasAdmin?: boolean, hasManyOrgs?: boolean }, authStatements?: AuthStatements[]): void {
    const { hasAdmin = false, hasManyOrgs = false } = extras || {};
    this.isManyOrgs = hasManyOrgs;
    this.isAdmin = hasAdmin;
    this.authStatements = authStatements;
  }

  clearOrganization() {
    this.organization = undefined;
    this.cycles.set(undefined);
  }

  setHasIncentiveGroup(reportGroupIncentive: Record<string, any>[]) {
    this.hasIncentiveGroup = !!reportGroupIncentive && reportGroupIncentive.length > 0;
  }

  get checkHasIncentiveGroup(): boolean | null {
    return this.hasIncentiveGroup;
  }

  setOrganization(organization?: Organization): boolean {
    if (!!organization && this.organization?.id === organization.id) {
      return true;
    }

    if (organization) {
      this.organization = organization;
      this.organizationLast = organization;
      this.getAmplitudeInfo(organization);
    } else {
      this.organization = this.organizationLast ?? DEFAULT_ORGANIZATION;
    }


    return true;
  }

  getOrganization(): Organization | undefined {
    return this.organization || DEFAULT_ORGANIZATION;
  }

  public getTitleDefaultPage(title?: string): string {
    if (window.innerWidth < 600) { // Disable for mobile
      return title;
    }
    if (this.getOrganization().type === 'HEALTH_OPERATOR') {
      return `Painel da operadora - ${ this.getOrganization().name }`;
    }

    if (['HUMAN_RESOURCES', 'GROUP'].includes(this.getOrganization().type)) {
      return `Painel da empresa - ${ this.getOrganization().name }`;
    }

    return title;
  }

  get isHP(): boolean {
    if (window.innerWidth < 600) { // Disable for mobile
      return false;
    }
    return this.getOrganization().type === 'HEALTH_OPERATOR';
  }

  get isHPMobile(): boolean {
    if (window.innerWidth > 600) { // Disable for mobile
      return false;
    }
    return this.getOrganization().type === 'HEALTH_OPERATOR';
  }

  public mobileTitle(title: string): string {
    if (window.innerWidth > 600) { // Disable for mobile
      return title;
    }
    if (this.getOrganization().type === 'HEALTH_OPERATOR') {
      return `Painel da operadora`;
    }

    if (['HUMAN_RESOURCES', 'GROUP'].includes(this.getOrganization().type)) {
      return `Painel da empresa`;
    }
  }

  getAmplitudeInfo(organization: Organization) {
    if (this.isAdmin) {
      return;
    }

    const { parent, type, id, name } = organization;
    let complementParent = {};
    if (parent) {
      const { type: pType, id: pId, name: pName } = parent;
      complementParent = {
        'parent_org_id': pId?.toString(),
        'parent_org_type': pType,
        'parent_org_name': pName
      };
    }

    this.amplitudeService.sendEvent('org_context_selected', {
      'org_id': id?.toString(),
      'org_type': type,
      'org_name': name,
      ...complementParent
    });
  }

  getDefaultUrl(organization?: Organization): string {
    if (!organization) {
      organization = this.getOrganization();
    }
    return this.urlService.organizationsBase(organization.id);
  }

  private isDevEnvironment(): boolean {
    return environment.env === 'dev';
  }
}

const DEFAULT_ORGANIZATION: Organization = {
  nameUrlSafe: 'mar',
  name: 'Gestor | Mar Saúde',
  logo: 'https://mar-static.s3.us-east-2.amazonaws.com/organization-logo/mar.png',
  favicon: 'https://mar-static.s3.us-east-2.amazonaws.com/organization-favicon/mar.ico'
};
